import { Component, Input, OnInit, AfterViewInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Chart, ChartEvent, registerables } from 'chart.js';
import { BarChartConfig, BarChartDataPoint } from '../../types/chart-types';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { AuthenticationService } from 'src/app/core/services/authentication.service'; 

@Component({
    selector: 'app-bar-chart',
    templateUrl: './bar-chart.component.html',
    styleUrls: ['./bar-chart.component.scss'],
    standalone: false
})
export class BarChartComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() data: BarChartDataPoint[] = [];
  @Input() labels: (string | string[])[] = [];
  @Input() yAxisLabel: string = '';
  @Input() barColor: string = '#007bff';
  @Input() unique_id: string = '';
  @Input() barChartConfig: BarChartConfig = {};
  @Input() enableTooltip: boolean = true;
  @Input() chartName: string = '';
  @Input() showLabelInTooltip: boolean = false;

  chart!: Chart<"bar", number[], unknown> | undefined;
  config: BarChartConfig = {};

  constructor(
    private _mixpanelService: MixpanelService,
    private _authenticationservice: AuthenticationService
  ) {
    Chart.register(...registerables); // Register necessary components
  }

  ngOnInit(): void {
    this.config = {
      yAxisPaddingBottom : 20,
      layoutPaddingLeft : 0,
      ...this.barChartConfig
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] || changes['labels']) {
      this.updateChart();
    }
  }

  ngAfterViewInit() {
    this.implementBarChart();
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }

  externalTooltipHandler(context: { chart: Chart; tooltip: any }) {

    const { chart, tooltip } = context;
    let tooltipEl = chart.canvas.parentNode?.querySelector("#chart-js-tooltip") as HTMLDivElement;

    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = "chart-js-tooltip";
      tooltipEl.style.cssText = `
        background: rgba(255, 255, 255);
        border-radius: 3px;
        color: black;
        opacity: 1;
        position: absolute;
        transform: translate(-50%, 0);
        transition: all .1s ease;
        white-space: nowrap;
        box-shadow: 0px 3px 10px 0px rgba(106, 106, 106, 0.15);
      `;

      const table = document.createElement("table");
      table.style.margin = "0";

      tooltipEl.appendChild(table);
      chart.canvas.parentNode?.appendChild(tooltipEl);
    }

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = '0';
      return;
    }

    const dataIndex = tooltip.dataPoints[0].dataIndex;
    const currentObject = this.data[dataIndex];
    const currentIssue = this.labels[dataIndex];

     //Mixpanel tracking on tooltip hover
     this._mixpanelService.track(`${this.chartName} Bar Chart Tooltip Hovered`, {
      chartName: this.chartName,
      type: this.labels[dataIndex],
      ImpersonatedCustomer: this._authenticationservice.customerName??" ",
      ImpersonatedUser:  this._authenticationservice.userName??" ",

    });

    if (tooltip.body) {
      const tableRoot = tooltipEl.querySelector("table");
      if (!tableRoot) return;

      // Remove old children
      while (tableRoot.firstChild) {
        tableRoot.removeChild(tableRoot.firstChild);
      }

      const tooltipEle = document.createElement("div");
      tooltipEle.setAttribute('id', 'end-date-tooltip');
      tooltipEle.innerHTML = `
        <div style="display:flex; align-items:bottom; gap:8px; padding-bottom: 5px;">
          <span class="total-calls-tooltip" style="font-size:24px; color:#313233;">${currentObject.calls}</span>
          <span style="font-size:12px; color:#8E9393">total calls</span>
        </div>
        across ${currentObject.locations} locations 
        ${this.showLabelInTooltip ? '<br/>with ' + currentIssue + ' issues' : ''}
      `;
      tableRoot.appendChild(tooltipEle);
    }

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

    // Calculate the position of the tooltip
    const tooltipX = positionX + tooltip.caretX;
    const tooltipY = positionY + tooltip.caretY;

    // Set tooltip content and styling
    tooltipEl.style.opacity = "1";
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = tooltip.options.padding + "px " + tooltip.options.padding + "px";

    // Get the width of the tooltip after setting its content
    const tooltipWidth = tooltipEl.offsetWidth;
    const chartWidth = chart.width;

    // Check if there's enough space on the right side
    if (tooltipX + tooltipWidth + 10 > positionX + chartWidth) {
      // Not enough space on the right, position tooltip to the left of the bar
      tooltipEl.style.left = `${tooltipX}px`;
      tooltipEl.style.top = `${tooltipY}px`;
      tooltipEl.style.transform = 'translate(calc(-100% - 10px), -50%)';
    } else {
      // Enough space on the right, position tooltip to the right of the bar
      tooltipEl.style.left = `${tooltipX}px`;
      tooltipEl.style.top = `${tooltipY}px`;
      tooltipEl.style.transform = 'translate(10px, -50%)';
    }
  }

  implementBarChart() {
    const canvas = document.getElementById(
      'cf-bar-chart-' + this.unique_id
    );

    if (canvas != null && canvas instanceof HTMLCanvasElement) {
      const ctx = canvas.getContext('2d');
      const callsData = this.data.map(d => d.calls);
      const maxValue = Math.max(...callsData);

      if (ctx == null) {
        throw new Error('Cannot get 2d context from canvas');
      }

      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.labels,
          datasets: [
            {
              // Background bars
              data: Array(this.data.length).fill(maxValue),
              backgroundColor: 'rgba(242, 247, 255, 1)',
              borderWidth: 0,
              maxBarThickness: 10,
              borderRadius: 5,
              borderSkipped: false,
              barPercentage: 0.5,
              categoryPercentage: 1,
              hoverBackgroundColor: 'rgba(242, 247, 255, 1)',
              order: 2,
            },
            {
              // Data bars
              data: callsData,
              backgroundColor: this.barColor,
              borderWidth: 0,
              maxBarThickness: 10,
              borderRadius: 5,
              borderSkipped: false,
              barPercentage: 0.5,
              categoryPercentage: 1,
              hoverBackgroundColor: this.barColor,
              order: 1,
            }
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              position: 'left',
              offset: true,
              title: {
                display: true,
                text: this.yAxisLabel,
                font: {
                  family: 'Roboto',  // Set the font family to Roboto
                },
                padding: {
                  bottom: this.config.yAxisPaddingBottom
                }
              },
              border: { display: false },
              grid: {
                display: false,
                offset: false
              },
              ticks: {
                padding: 0, // Increase this value to move numbers further from the axis
                font: {
                  size: 10,
                  family: 'Inter',
                },
                color: '#000000'
              },
              stacked: false
            },
            x: {
              border: { display: false },
              grid: {
                display: false,
              },
              ticks: {
                font: {
                  size:  10,
                  family: 'Roboto',
                },
                color: '#313233',
                // Tilt tolerance is more aggressive in chart.js v4, to preserve the original orientation, we set both maxRotation and minRotation to the same value if xAxisMaxRotation is provided in the config.
                ...(this.config.xAxisMaxRotation !== undefined ? { maxRotation: this.config.xAxisMaxRotation, minRotation: this.config.xAxisMaxRotation } : {}),
              },
              stacked: true,
            }
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: 'nearest',
              external: (ctx : any) => this.externalTooltipHandler(ctx)
            }
          },
          layout: {
            padding: {
              top: 20,
              bottom: 10,
              left: this.config.layoutPaddingLeft
            },
          },
        },
      });
    }
  }

  updateChart() {
    if (this.chart) {
      const callsData = this.data.map(d => d.calls);
      const maxValue = Math.max(...callsData);

      this.chart.data.datasets[0].data = Array(this.data.length).fill(maxValue);
      this.chart.data.datasets[1].data = callsData;
      this.chart.data.labels = this.labels;

      this.chart.update();
    } else {
      this.implementBarChart();
    }
  }
}
